"use client";

import { Clear, Search } from "@mui/icons-material";
import { Box, Button, IconButton, InputBase, Paper } from "@mui/material";
import type { Column, Table } from "@tanstack/react-table";
import * as React from "react";

import { useDebouncedCallback } from "@/hooks/use-debounced-callback";

import { DataTableFacetedFilter } from "./data-table-faceted-filter";
import { DataTableViewOptions } from "./data-table-view-options";

type DataTableToolbarProps<TData> = {
  table: Table<TData>;
} & React.ComponentProps<"div">;

export function DataTableToolbar<TData>({
  table,
  children,
  className,
  ...props
}: DataTableToolbarProps<TData>) {
  const isFiltered = table.getState().columnFilters.length > 0;

  const columns = table.getAllColumns().filter((column) => column.getCanFilter());

  const onReset = () => {
    table.resetColumnFilters();
  };

  return (
    <Box
      className={className}
      role="toolbar"
      sx={{
        display: "flex",
        width: "100%",
        flexWrap: "wrap",
        alignItems: { xs: "stretch", md: "center" },
        justifyContent: "space-between",
        gap: 1.25,
      }}
      {...props}
    >
      <Box sx={{ display: "flex", flex: 1, flexWrap: "wrap", alignItems: "center", gap: 1 }}>
        {columns.map((column) => (
          <DataTableToolbarFilter column={column} key={column.id} />
        ))}
        {isFiltered && (
          <Button
            onClick={onReset}
            size="small"
            startIcon={<Clear />}
            sx={{ borderStyle: "dashed", textTransform: "none" }}
            variant="outlined"
          >
            Reset
          </Button>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
        {children}
        <DataTableViewOptions table={table} />
      </Box>
    </Box>
  );
}

type DataTableToolbarFilterProps<TData> = {
  column: Column<TData>;
};

type DataTableToolbarTextFilterProps<TData> = {
  column: Column<TData>;
  placeholder: string;
};

function DataTableToolbarTextFilter<TData>({
  column,
  placeholder,
}: DataTableToolbarTextFilterProps<TData>) {
  const [value, setValue] = React.useState(() => String(column.getFilterValue() ?? ""));

  const externalValue = String(column.getFilterValue() ?? "");

  React.useEffect(() => {
    if (!externalValue) setValue("");
  }, [externalValue]);

  const debouncedSetFilter = useDebouncedCallback(
    (value: string) => column.setFilterValue(value || undefined),
    150
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
    debouncedSetFilter(event.target.value);
  };

  return (
    <Paper
      component="form"
      onSubmit={(event) => {
        event.preventDefault();
      }}
      sx={{
        p: "2px 4px",
        display: "flex",
        alignItems: "center",
        minWidth: { xs: 180, lg: 240 },
        borderRadius: 999,
      }}
      variant="outlined"
    >
      <InputBase
        onChange={handleChange}
        placeholder={placeholder}
        sx={{ ml: 1, flex: 1 }}
        value={value}
      />
      <IconButton aria-label="search" size="small" type="button">
        <Search fontSize="small" />
      </IconButton>
    </Paper>
  );
}

function DataTableToolbarFilter<TData>({ column }: DataTableToolbarFilterProps<TData>) {
  const columnMeta = column.columnDef.meta;

  if (!columnMeta?.variant) return null;

  switch (columnMeta.variant) {
    case "text":
      return (
        <DataTableToolbarTextFilter
          column={column}
          placeholder={columnMeta.placeholder ?? columnMeta.label ?? column.id}
        />
      );

    case "select":
    case "multiSelect":
      return (
        <DataTableFacetedFilter
          column={column}
          multiple={columnMeta.variant === "multiSelect"}
          options={columnMeta.options ?? []}
          title={columnMeta.label ?? column.id}
        />
      );

    case "date":
    case "dateRange":
      return null;

    default:
      return null;
  }
}
